import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const FALLBACK_TESTIMONIALS = [
  {
    _id: "f1", name: "Jelitza Gutierrez", initials: "JG", avatarBg: "#2563EB",
    timeLabel: "a month ago", rating: 5,
    text: "I had a great experience with Paladin Professional Insurance Solutions. Denise and Lionel are incredibly kind, knowledgeable, and professional throughout the entire process. They truly go above and beyond for their clients.",
  },
  {
    _id: "f2", name: "Aj Laboriante", initials: "AL", avatarBg: "#B45309",
    timeLabel: "a month ago", rating: 5,
    text: "I do not leave reviews often, but when service is this good, it deserves to be recognized. They truly operate at a different level. From the very first conversation, I felt like I was not just another client.",
  },
  {
    _id: "f3", name: "Plumbing Done Right", initials: "PD", avatarBg: "#7C3AED",
    timeLabel: "a month ago", rating: 5,
    text: "Since I have started my Plumbing Business a few years ago Paladin Insurance Services Solutions has been absolutely Great! From Denise, Lionel and all their Staff are incredibly helpful and professional.",
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-1 mb-5">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill="#F59E0B" stroke="none">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </div>
  );
}

function QuoteIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
    </svg>
  );
}

function TestimonialCard({ testimonial, style, mobile }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex-shrink-0 flex flex-col justify-between cursor-default transition-all duration-300"
      style={{
        width: mobile ? "85vw" : "420px",
        backgroundColor: hovered ? "#012E72" : "#ffffff",
        padding: mobile ? "28px 24px" : "44px 40px 36px",
        boxShadow: hovered ? "0 20px 60px rgba(0,0,0,0.25)" : "0 4px 20px rgba(0,0,0,0.06)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex flex-col gap-0">

        {/* Top row: quote icon + stars */}
        <div className="flex items-start justify-between mb-4">
          <span
            className="transition-colors duration-300"
            style={{ color: hovered ? "rgba(255,255,255,0.6)" : "rgba(1,46,114,0.25)" }}
          >
            <QuoteIcon size={20} />
          </span>
          <Stars count={testimonial.rating ?? testimonial.stars ?? 5} />
        </div>

        {/* Review text */}
        <p
          className="font-constantia italic text-[0.88rem] leading-[1.8] mb-5 transition-colors duration-300"
          style={{ color: hovered ? "rgba(255,255,255,0.8)" : "rgba(1,46,114,0.7)" }}
        >
          "{testimonial.text}"
        </p>

        {/* Divider */}
        <div
          className="h-px mb-4 transition-colors duration-300"
          style={{
            background: hovered
              ? "linear-gradient(to right, transparent, rgba(255,255,255,0.15), transparent)"
              : "linear-gradient(to right, transparent, rgba(1,46,114,0.12), transparent)",
          }}
        />

        {/* Author row */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-[10px]">
            {/* Avatar */}
            <div
              className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0 transition-shadow duration-300"
              style={{
                backgroundColor: testimonial.avatarBg,
                boxShadow: hovered ? `0 0 14px ${testimonial.avatarBg}88` : "none",
              }}
            >
              <span className="font-cinzel font-bold text-[0.62rem] text-white tracking-[0.05em]">
                {testimonial.initials}
              </span>
            </div>

            {/* Name */}
            <span
              className="font-cinzel font-bold text-[0.72rem] tracking-[0.04em] transition-colors duration-300"
              style={{ color: hovered ? "#ffffff" : "#012E72" }}
            >
              {testimonial.name}
            </span>
          </div>

          {/* Time label */}
          <span
            className="font-constantia text-[0.72rem] whitespace-nowrap transition-colors duration-300"
            style={{ color: hovered ? "rgba(255,255,255,0.35)" : "rgba(1,46,114,0.35)" }}
          >
            {testimonial.timeLabel ?? testimonial.time ?? ""}
          </span>
        </div>

      </div>
    </div>
  );
}

export default function TestimonialSection() {
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [headerRef, headerInView] = useInView(0.1);
  const [bodyRef, bodyInView] = useInView(0.08);
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/testimonials`)
      .then((r) => r.ok ? r.json() : null)
      .then((json) => { if (json?.data?.length) setTestimonials(json.data); })
      .catch(() => {});
  }, []);

  return (
    <section className="relative w-full overflow-hidden" style={{ backgroundColor: "#012E72" }}>

      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Top label */}
      <div
        ref={headerRef}
        className="relative z-10 flex justify-end items-center gap-3 px-6 md:px-16 pt-10 md:pt-14 pb-0"
        style={{
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? "translateY(0)" : "translateY(-16px)",
          transition: "opacity 0.6s ease, transform 0.6s ease",
        }}
      >
        <span className="font-cinzel uppercase tracking-widest text-white" style={{ fontSize: "12px", opacity: 0.6 }}>
          Testimonials
        </span>
      </div>

      {/* Headline */}
      <div
        className="relative z-10 text-right px-6 md:px-16 pt-4 md:pt-6 pb-8 md:pb-14"
        style={{
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
        }}
      >
        <h2
          className="font-cinzel font-bold text-white leading-tight"
          style={{ fontSize: "clamp(1.5rem, 4vw, 3.2rem)" }}
        >
          Hear{" "}
          <span className="font-constantia italic font-normal">directly</span>
          {" "}from the
          <br />
          people we{" "}
          <span className="font-constantia italic font-normal">proudly</span>
          {" "}serve.
        </h2>
      </div>

      {/* Body */}
      <div ref={bodyRef} className="relative z-10">

        {/* MOBILE (< md) */}
        <div className="flex flex-col md:hidden px-6 pb-10">
          <div
            className="font-cinzel font-black select-none leading-none mb-2"
            style={{ fontSize: "80px", color: "rgba(255,255,255,0.12)" }}
          >
            "
          </div>
          <h3
            className="font-cinzel font-bold text-white uppercase leading-tight mb-6"
            style={{ fontSize: "22px", letterSpacing: "0.04em" }}
          >
            WHAT<br />OUR CLIENTS<br />SAY<br />ABOUT US
          </h3>

          <div className="overflow-hidden mb-6">
            <div
              className="flex gap-4"
              style={{
                transform: `translateX(calc(-${current} * (85vw + 16px)))`,
                transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {testimonials.map((t, i) => (
                <TestimonialCard
                  key={t._id ?? t.id}
                  testimonial={t}
                  mobile
                  style={{
                    opacity: bodyInView ? 1 : 0,
                    transform: bodyInView ? "translateY(0)" : "translateY(24px)",
                    transition: `opacity 0.6s ease ${0.1 + i * 0.1}s, transform 0.6s ease ${0.1 + i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/testimonial")}
              className="font-cinzel font-bold text-white uppercase tracking-widest text-[11px] border border-white/40 px-5 py-3 hover:bg-white hover:text-[#012E72] transition-all duration-300"
            >
              View All
            </button>
            <div className="flex gap-2">
              <button onClick={prev} className="flex items-center justify-center border border-white/30 hover:bg-white/10 transition-colors duration-200" style={{ width: "38px", height: "38px" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button onClick={next} className="flex items-center justify-center border border-white/30 hover:bg-white/10 transition-colors duration-200" style={{ width: "38px", height: "38px" }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>
        </div>

        {/* DESKTOP (md+) */}
        <div className="hidden md:flex items-stretch">

          {/* Left column — 30% */}
          <div
            className="flex flex-col justify-between flex-shrink-0 px-16 py-14"
            style={{
              width: "30%",
              opacity: bodyInView ? 1 : 0,
              transform: bodyInView ? "translateX(0)" : "translateX(-24px)",
              transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
            }}
          >
            <div
              className="font-cinzel font-black leading-none select-none"
              style={{ fontSize: "120px", color: "rgba(255,255,255,0.12)", lineHeight: 1, marginTop: "-10px" }}
            >
              "
            </div>

            <div>
              <h3
                className="font-cinzel font-bold text-white uppercase leading-tight mb-10"
                style={{ fontSize: "32px", letterSpacing: "0.04em" }}
              >
                WHAT<br />OUR CLIENTS<br />SAY<br />ABOUT US
              </h3>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/testimonial")}
                  className="font-cinzel font-bold text-white uppercase tracking-widest text-[11px] border border-white/40 px-5 py-3 hover:bg-white hover:text-[#012E72] transition-all duration-300"
                >
                  View All
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={prev} className="flex items-center justify-center border border-white/30 hover:bg-white/10 transition-colors duration-200" style={{ width: "38px", height: "38px" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  <button onClick={next} className="flex items-center justify-center border border-white/30 hover:bg-white/10 transition-colors duration-200" style={{ width: "38px", height: "38px" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cards — 70% */}
          <div className="overflow-hidden py-14 pl-0" style={{ width: "70%" }}>
            <div
              className="flex gap-5 pl-10"
              style={{
                transform: `translateX(calc(-${current} * 440px))`,
                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              {testimonials.map((t, i) => (
                <TestimonialCard
                  key={t._id ?? t.id}
                  testimonial={t}
                  style={{
                    opacity: bodyInView ? 1 : 0,
                    transform: bodyInView ? "translateY(0)" : "translateY(32px)",
                    transition: `opacity 0.6s ease ${0.25 + i * 0.1}s, transform 0.6s ease ${0.25 + i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
